import React, { useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { request } from "../../../../../utils/Request";
import { dateToUtcDate, setLastUpdate, utcDateToLocalDate } from "../../../../../utils/Helper";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { get, isEmpty } from 'lodash';
import moment from "moment";
import { Sidebar } from "primereact/sidebar";
import { InputField } from "../../../../../components/Shared/InputField";
import { Col, Row } from "react-bootstrap";
import { InputTextAreaField } from "../../../../../components/Shared/InputTextAreaField";
import { Button } from "primereact/button";
import InputDateTime from "../../../../../components/Shared/InputDateTime";
import Location from "../../../../../components/Common/Display/Location";
import AlpOutboundLoadDocument from "../AlpOutboundLoad/AlpOutboundLoadDocument";

interface IFormProps {
    id: string | null,
    t: any | null,
    onRefresh?: any | null,
    dropdowns?: any | null,
    onClose?: any | null,
    toastify?: any | null,
    providerUploadSlug?: string | null,
}

type TInputForm = {
    date?: any,
    comments?: any,
    id?: any,
    cmr?: any,
};

const DEFAULT_FORM_STATE: TInputForm = {
    date: null,
    comments: null,
    id: null,
    cmr: null,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        date: yup.date(t("the field value is not valid", { field: t('date') }))
            .required(t('the field is required.', { field: t('date') })).nullable(),
        comments: yup.string().nullable(),
        cmr: yup.string().nullable(),
    });
};

function AlpReexportLoadInTransit(props: IFormProps) {
    const { dropdowns, toastify } = props;
    const { t } = useTranslation();
    const formRef: any = useRef();
    const documentRef: any = useRef();
    const [entity, setEntity] = useState<any>(null);
    const [initFormState, setInitFormState] = useState<any>({ ...DEFAULT_FORM_STATE });
    const [panelState, setPanelState] = useState<boolean>(false);
    const providerUploadSlug = props.providerUploadSlug
        ?? entity?.provider_slug
        ?? entity?.providerSlug
        ?? entity?.provider?.slug
        ?? dropdowns?.providers?.find((provider: any) => provider.id === entity?.provider_id)?.slug
        ?? dropdowns?.providers?.find((provider: any) => provider.id === entity?.provider_id)?.id
        ?? entity?.source_type
        ?? 'emil_frey';

    const onClose = () => {
        setPanelState(false);
        documentRef.current?.resetDocuments();

        if ( props.onClose ) {
            props.onClose();
        }
    };

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        const documents = documentRef.current?.getDocuments() || [];

        try {
            await request({
                method: 'POST',
                url: `/suppliers/alp/reexport/loads/${ props.id }/in/transit`,
                data: {
                    ...payloads,
                    date: (( payloads.date ) ? dateToUtcDate(payloads.date, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss') : null),
                    documents: documents.map((item: any) => ({
                        id: item.document_id,
                        title: item.title
                    }))
                },
            });

            toastify(t("record updated", { item: t('self pickup load') }), "info");
            setLastUpdate(payloads.id, 'AlpReexportLoad');
            resetForm();
            onClose();

            if ( props.onRefresh ) {
                props.onRefresh();
            }
        } catch (e: any) {
            if ( e.status === 422 ) {
                setErrors(e.data.errors);
            }
            if ( e.status === 404 ) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const getDetail = async (id: string) => {
        try {
            const response = await request({
                url: `/suppliers/alp/reexport/loads/${ id }`
            });
            const { data } = response.data;

            setPanelState(true);
            setTimeout(() => {
                setEntity(data);

                setInitFormState({
                    id: data.id,
                    date: data.in_transit_at
                        ? utcDateToLocalDate(data.in_transit_at, 'YYYY-MM-DD HH:mm:ss')
                        : moment().format('YYYY-MM-DD HH:mm:ss'),
                    cmr: data.cmr ?? null
                });
                documentRef.current?.resetDocuments();
            }, 500);
        } catch (error: any) {
            if ( error.status === 404 ) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    useEffect(() => {
        setEntity(null);
        const currentId = props.id;
        if ( typeof currentId === 'string' && !isEmpty(currentId) ) {
            getDetail(currentId);
        }
    }, [props.id]);

    return (
        <div>
            <Sidebar
                header={
                    <div className="bg-body-tertiary card-header">
                        <h5 className={ "card-title mt-0" }>{ t('start journey') }{ entity && ` #${ entity.load_number }` }</h5>
                        <p>{ t("using this form you can add or update the record") }</p>
                    </div>
                }
                visible={ panelState } position="right" onHide={ onClose } blockScroll={ true }
                style={ { width: '800px' } } closeOnEscape={ false }
            >
                <div style={ {
                    height: '3px',
                    background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                } }></div>
                <Formik
                    innerRef={ formRef }
                    enableReinitialize={ true }
                    initialValues={ initFormState }
                    onSubmit={ onSubmit }
                    validationSchema={ validationSchema(t) }
                >
                    { (props: FormikProps<TInputForm>) => {
                        return (
                            <Form onSubmit={ props.handleSubmit }>
                                {
                                    entity &&
                                    <Row className={ "mt-4 mb-7" }>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field
                                                placeholder={ t("pick a date") }
                                                component={ InputDateTime }
                                                name={ `date` }
                                                type={ "datetime" }
                                                label={ t("date") }
                                                maxDate={ moment().endOf('day').tz(moment.tz.guess()).toDate() }
                                                isRequired
                                            />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="cmr"
                                                   label={ t('cmr') }
                                                   placeholder={ t("enter") + ' ' + t("cmr").toLowerCase() }
                                            />
                                        </Col>
                                        <Col sm={ 12 } md={ 12 }>
                                            <Field component={ InputTextAreaField }
                                                   name="comments"
                                                   label={ t('comments') }
                                                   placeholder={ t("enter") + ' ' + t("comments").toLowerCase() }
                                            />
                                        </Col>

                                        <Col sm={ 12 } className={ "mt-3 mb-3" }>
                                            <AlpOutboundLoadDocument
                                                ref={ documentRef }
                                                dropdowns={ dropdowns }
                                                providerUploadSlug={ providerUploadSlug }
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 12 } className={ "mb-5" }>
                                            <hr />
                                            <h4 className="card-title">{ t('orders') }</h4>
                                            <div className={ 'table-responsive' }>
                                                <table className={ "fl-table" }>
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>{ t('from location') }</th>
                                                            <th>{ t('to location') }</th>
                                                            <th>{ t('brand') }</th>
                                                            <th>{ t('model') }</th>
                                                            <th>{ t('order code') }</th>
                                                            <th>{ t('vin number') }</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {
                                                            entity.alp_reexport_load_orders.map((row: any, index: number) => (
                                                                <tr key={ index }>
                                                                    <th>{ index + 1 }</th>
                                                                    <td>
                                                                        <Location
                                                                            item={ get(entity, ['from_location']) }
                                                                            titled={ 1 } />
                                                                    </td>
                                                                    <td>
                                                                        <Location
                                                                            item={ get(entity, ['to_location']) }
                                                                            titled={ 1 } />
                                                                    </td>
                                                                    <td className={ "text-capitalize" }>{ get(row, ['order', 'brand', 'title']) }</td>
                                                                    <td className={ "text-capitalize" }>{ get(row, ['order', 'brand_model', 'title']) } - { get(row, ['order', 'brand_model', 'version_code']) } </td>
                                                                    <td>{ get(row, ['order', 'order_cd']) }</td>
                                                                    <td>{ get(row, ['order', 'vin_number']) }</td>
                                                                </tr>
                                                            ))
                                                        }
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                    </Row>
                                }
                                <div className="filter-card-footer" style={ { width: '785px' } }>
                                    <Button tooltip={ t("to data save into database") }
                                            size={ "small" } outlined
                                            tooltipOptions={ { position: 'top' } }
                                            disabled={ props.isSubmitting }
                                    >
                                        <i className="pi pi-save me-2" />
                                        { t("let's save") }
                                    </Button>
                                    <Button type={ "button" }
                                            tooltip={ t("operation cancel") } className={ "ms-2" }
                                            size={ "small" } severity={ "danger" } outlined
                                            tooltipOptions={ { position: 'top' } }
                                            onClick={ onClose }
                                            disabled={ props.isSubmitting }
                                    >
                                        <i className="pi pi-arrow-left me-2" />
                                        { t("cancel") }
                                    </Button>
                                </div>
                            </Form>
                        );
                    } }
                </Formik>
            </Sidebar>
        </div>
    );
}

AlpReexportLoadInTransit.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpReexportLoadInTransit);
